import React, { useState, useEffect } from "react";
import apiClient from "../../api_client";
import { Table, Space, Card, Row, Col, Input, Checkbox, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { API_URL } from "../../url_config";
import {NavigationStepsCds} from "../../components/navbars/navbar_cds"
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";

const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne pour les zones de texte ===
function TableTetraBus({ record, onChange, setIsUpdating }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  // Synchroniser si record change
  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = async (field, value) => {
    setIsUpdating(true); // bloque le rafraîchissement pendant la saisie
    onChange(record.key, field, value);
    try {
      await apiClient.patch(
        `${API_URL}/validations/tetra_bus/${record.id}/`,
        { [field]: value }
      );
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    } finally {
      // on attend un peu avant de réactiver le rafraîchissement
      setTimeout(() => setIsUpdating(false), 4000);
    }
  };

  return (
    <div style={{ padding: "20px" }}>


      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={5}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Informations</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                (Supervision)
              </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={5}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const TetraBusCds = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); // ✅ bloque le rafraîchissement auto

  // Charger les données
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isUpdating) return; // ✅ ne recharge pas pendant qu’on écrit ou met à jour

      try {
          const res = await apiClient.get(`${API_URL}/validations/tetra_bus/?ot_tag=cds`);
        if (!isMounted) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          poste: item.poste,
          affichage_ihm: item.affichage_ihm,
          presence_des_ligne: item.presence_des_ligne,
          presence_des_coquilles: item.presence_des_coquilles,
          appel_individuel: item.appel_individuel,
          appel_general: item.appel_general,
          reception_d_un_appel_mr: item.reception_d_un_appel_mr,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    setLoading(true);
    fetchData();

    // 🔁 Rechargement auto toutes les 5 secondes (au lieu de 2s)
    const interval = setInterval(() => {
      fetchData();
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isUpdating]);

  // ✅ Mise à jour locale + serveur
  const handleDataChange = async (key, field, value) => {
    setIsUpdating(true);

    // Mise à jour locale immédiate
    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    try {
      const updatedItem = data.find((item) => item.key === key);
      if (updatedItem) {
          await apiClient.patch(
          `${API_URL}/validations/tetra_bus/${updatedItem.id}/`,
          { [field]: value }
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    } finally {
      // ✅ redonne le feu vert pour le rafraîchissement après un court délai
      setTimeout(() => setIsUpdating(false), 2000);
    }
  };

  const columns = [
    { title: "Poste", dataIndex: "poste" },
    {
      title: "Affichage IHM",
      dataIndex: "affichage_ihm",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "affichage_ihm", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Présence des lignes",
      dataIndex: "presence_des_ligne",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_des_ligne", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Présence des coquilles",
      dataIndex: "presence_des_coquilles",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_des_coquilles", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Appel ligne",
      dataIndex: "appel_general",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "appel_general", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Appel individuel",
      dataIndex: "appel_individuel",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "appel_individuel", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Réception appel MR",
      dataIndex: "reception_d_un_appel_mr",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "reception_d_un_appel_mr", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
  title: "ETAT",
  dataIndex: "signal",
  render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
  },
},

  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsCds selectedKey="tetra_bus" />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TableTetraBus
              record={record}
              onChange={handleDataChange}
              setIsUpdating={setIsUpdating}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />



                  <div style={{ padding: 20 }}>

                        <ModifyItemPage
                          apiUrl={`${API_URL}/validations/tetra_bus/`}
                          ot_tag="cds"
                          title="Gestion"
                          rowKey="id"
                          trigger={<Button icon={<EditOutlined />} shape="circle" />}
                          columns={[
                            { title: "Poste", dataIndex: "poste", key: "poste" },
                          ]}
                          formFields={[
                            { name: "poste", label: "Poste", placeholder: "Entrez le poste" },
                          ]}
                        />
                  </div>




    </div>
  );
};

export default TetraBusCds;
